import React, { useState, useMemo } from 'react'
import { Search, Plus, Building2, Eye, Edit, Trash2, MoreHorizontal } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectOption,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
} from '../components/ui'
import { LoadingSpinner, LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useDepartments, type Department, type DepartmentFilters } from '../hooks/useDepartments'
import { useLatestSync } from '../hooks/useSyncStatus'
import { DepartmentForm } from '../components/departments/DepartmentForm'
import { DeleteDepartmentDialog } from '../components/departments/DeleteDepartmentDialog'

export function Departments() {
  const [filters, setFilters] = useState<DepartmentFilters>({
    page: 1,
    limit: 20,
    sort: 'name:asc',
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [parentFilter, setParentFilter] = useState<string>('')
  const [showOnlyRoot, setShowOnlyRoot] = useState<boolean | undefined>(undefined)

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null)

  // Debounced search
  const debouncedFilters = useMemo(() => {
    const timer = setTimeout(() => {
      return {
        ...filters,
        search: searchTerm || undefined,
        parent_id: parentFilter || undefined,
        is_root: showOnlyRoot,
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [filters, searchTerm, parentFilter, showOnlyRoot])

  const { data, isLoading, error, refetch } = useDepartments(filters)
  const { data: syncData } = useLatestSync()

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setFilters(prev => ({ ...prev, page: 1, search: e.target.value || undefined }))
  }

  const handleParentFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setParentFilter(value)
    setFilters(prev => ({
      ...prev,
      page: 1,
      parent_id: value || undefined
    }))
  }

  const handleRootFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    const boolValue = value === 'true' ? true : value === 'false' ? false : undefined
    setShowOnlyRoot(boolValue)
    setFilters(prev => ({
      ...prev,
      page: 1,
      is_root: boolValue
    }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatSyncTime = (syncData: any) => {
    if (!syncData?.last_sync_at) {
      return 'Нет данных'
    }

    const syncTime = new Date(syncData.last_sync_at)
    const now = new Date()
    const diffMs = now.getTime() - syncTime.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) {
      return 'Только что'
    } else if (diffMinutes < 60) {
      return `${diffMinutes} мин назад`
    } else if (diffHours < 24) {
      return `${diffHours} ч назад`
    } else if (diffDays < 7) {
      return `${diffDays} дн назад`
    } else {
      return formatDate(syncData.last_sync_at)
    }
  }

  // Action handlers
  const handleEdit = (department: Department) => {
    setSelectedDepartment(department)
    setIsEditModalOpen(true)
  }

  const handleDelete = (department: Department) => {
    setSelectedDepartment(department)
    setIsDeleteModalOpen(true)
  }

  const handleView = (department: Department) => {
    // TODO: Implement view modal or navigate to detail page
    console.log('View department:', department)
  }

  const getSourceBadge = (department: Department) => {
    if (department.code_1c) {
      return <Badge variant="secondary">1С</Badge>
    }
    return <Badge variant="outline">TCO</Badge>
  }

  const getDepartmentHierarchy = (department: Department) => {
    const parts = []
    if (department.departments) {
      parts.push(department.departments.name)
    }
    parts.push(department.name)
    return parts.join(' → ')
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Подразделения</h1>
          <p className="text-muted-foreground">
            Управление структурой подразделений организации
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки подразделений"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Подразделения</h1>
          <p className="text-muted-foreground">
            Управление структурой подразделений организации
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить подразделение
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
          <CardDescription>
            Используйте фильтры для поиска нужных подразделений
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по названию..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10"
              />
            </div>

            {/* Parent filter */}
            <Select
              placeholder="Родительское подразделение"
              value={parentFilter}
              onChange={handleParentFilter}
            >
              <SelectOption value="">Все подразделения</SelectOption>
              {/* TODO: Load parent departments for filter */}
            </Select>

            {/* Root filter */}
            <Select
              placeholder="Уровень иерархии"
              value={showOnlyRoot?.toString() || ''}
              onChange={handleRootFilter}
            >
              <SelectOption value="">Все уровни</SelectOption>
              <SelectOption value="true">Только корневые</SelectOption>
              <SelectOption value="false">Только дочерние</SelectOption>
            </Select>

            {/* Sort */}
            <Select
              value={filters.sort}
              onChange={(e) => setFilters(prev => ({ ...prev, sort: e.target.value }))}
            >
              <SelectOption value="name:asc">По названию (А-Я)</SelectOption>
              <SelectOption value="name:desc">По названию (Я-А)</SelectOption>
              <SelectOption value="created_at:desc">По дате создания (новые)</SelectOption>
              <SelectOption value="created_at:asc">По дате создания (старые)</SelectOption>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{data.total}</p>
                  <p className="text-sm text-muted-foreground">Всего подразделений</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="mr-1">1С</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.from1cCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Из 1С</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="mr-1">TCO</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.localCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Локальные</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Последняя синхронизация</p>
                  <p className="text-sm font-medium">
                    {formatSyncTime(syncData)}
                  </p>
                  {syncData?.last_sync_type && (
                    <p className="text-xs text-muted-foreground">
                      Тип: {syncData.last_sync_type}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список подразделений</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} подразделений`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название и иерархия</TableHead>
                    <TableHead>Руководитель</TableHead>
                    <TableHead>Источник</TableHead>
                    <TableHead>Дата создания</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((department) => (
                    <TableRow key={department.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium">{getDepartmentHierarchy(department)}</p>
                          {department.code_1c && (
                            <p className="text-sm text-muted-foreground">
                              Код 1С: {department.code_1c}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {department.employees_departments_manager_idToemployees ? (
                          <div>
                            <p className="font-medium">
                              {department.employees_departments_manager_idToemployees.last_name}{' '}
                              {department.employees_departments_manager_idToemployees.first_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              №{department.employees_departments_manager_idToemployees.employee_number}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Не назначен</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getSourceBadge(department)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(department.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(department)}
                            title="Просмотр"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(department)}
                            title="Редактировать"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(department)}
                            title="Удалить"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Дополнительные действия">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Подразделения не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или добавьте новое подразделение.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <DepartmentForm
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        mode="create"
      />

      <DepartmentForm
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        department={selectedDepartment}
        mode="edit"
      />

      <DeleteDepartmentDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        department={selectedDepartment}
      />
    </div>
  )
}